import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale } from "@genius-tracker/i18n/react";
import { fetchSessionWithPostLoginGrace, redirectToLogin, type SessionInfo } from "../lib/api.js";
import { identityFromSession, reconcileClientIdentity } from "../lib/tenantScopedStorage.js";

/**
 * Session provider — thin React context around the BFF session
 * endpoint. On mount we fetch `/v1/auth/session`; if it returns null
 * (401) we redirect the browser to `/v1/auth/login`. No child is
 * rendered until we have either a session or a redirect in flight.
 *
 * This is the web-side half of the BFF pattern documented in
 * docs/foundation/DECISION_LOG.md DL-015. Product code reads
 * session state via `useSession()` and never talks to cookies or
 * token material directly.
 */

type SessionState =
  | { status: "loading" }
  | { status: "authenticated"; session: SessionInfo }
  | { status: "redirecting" };

const SessionContext = createContext<SessionInfo | null>(null);

export function useSession(): SessionInfo {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession() used outside <SessionProvider>.");
  return ctx;
}

/**
 * Non-throwing variant for components that only *adapt* their copy
 * to the session (e.g. role-aware hints) and must keep rendering a
 * sensible default when no session is available. Production mounts
 * always sit under `<SessionProvider>`, so `null` effectively means
 * "render the least-privileged wording".
 */
export function useOptionalSession(): SessionInfo | null {
  return useContext(SessionContext);
}

/**
 * Test-only escape hatch — exported under a dunder name so callers
 * understand it bypasses the production SessionProvider's fetch +
 * redirect logic. Tests use this to mount product code that depends
 * on `useSession()` without booting the real BFF round trip.
 *
 * NOT for production use.
 */
export const __SessionContextForTesting = SessionContext;

export function SessionProvider({ children }: { children: ReactNode }) {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    const ctl = new AbortController();
    fetchSessionWithPostLoginGrace(ctl.signal)
      .then((session) => {
        if (ctl.signal.aborted) return;
        if (!session) {
          setState({ status: "redirecting" });
          redirectToLogin();
          return;
        }
        // GT-USER-PREFERENCES-STATE-PERSISTENCE-01 — reconcile this
        // browser's persisted client state with the active identity
        // (purge legacy UNSCOPED keys; retain every identity's own
        // scoped keys; drop the in-memory query cache only on a genuine
        // identity switch) BEFORE exposing the session to children.
        //
        // Doing it here rather than in a post-render effect guarantees
        // the cross-identity cache clear runs before AppShell/header
        // queries mount, so the fleet-summary status chips no longer get
        // wiped on the first render after login (the owner-reported
        // "chips missing until a manual refresh" regression). A normal
        // login already starts with an empty cache, so this is a no-op
        // there; it only matters for a re-auth without a full reload.
        reconcileClientIdentity(identityFromSession(session), queryClient);
        setState({ status: "authenticated", session });
      })
      .catch((err: unknown) => {
        if (ctl.signal.aborted) return;
        // Network error — show a minimal message and let the user retry.
        console.error("session.fetch.failed", err);
        setState({ status: "redirecting" });
        redirectToLogin();
      });
    return () => ctl.abort();
  }, [queryClient]);

  const session = state.status === "authenticated" ? state.session : null;

  const value = useMemo(() => session, [session]);

  if (!value) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minBlockSize: "100dvh",
          display: "grid",
          placeItems: "center",
          color: "var(--gt-sys-text-muted)",
          font: "500 14px/1.4 var(--gt-sys-font-family-sans, system-ui)",
        }}
      >
        <span>
          {state.status === "redirecting" ? t("auth.redirecting") : t("auth.session.loading")}
        </span>
      </div>
    );
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
